/* eslint-disable @typescript-eslint/no-explicit-any */
// react
import { useCallback, useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  DataGridPremium,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridCellParams,
  GridColDef,
  GridFilterModel,
  GridSortDirection,
  useGridApiRef,
} from '@mui/x-data-grid-premium';

// feature-components
import PortfolioAnalysisCombinedToolbar from '@/features/energyxplorer/portfolio/components/datagrid/overview-datagrid/PortfolioAnalysisCombinedToolbar';
import CustomColumnsPanel from '@/features/energyxplorer/portfolio/components/datagrid/type-datagrid/CustomColumnsPanel';
// components
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { useLocalStorageJson } from '@/hooks/useLocalStorageJson';

// relative
import CustomNoRowsOverlay from '../../../../../../components/ui/datagrid/DataGridNoRowsOverlay';
import { useDashboardColumns } from '../../../hooks/dashboard-columns';

interface Props {
  data: any;
  matrikelList: any[] | null | undefined;
  setSelectedMeter: (row: unknown) => void;
  setMeterType: (type: string) => void;
  isLoading: boolean;
}

const PortfolioAnalysisBuildingsDatagrid = ({
  data,
  matrikelList,
  setSelectedMeter,
  setMeterType,
  isLoading,
}: Props) => {
  const localeText = useDatagridLocaleText();

  const { t } = useTranslation('portfolio-analysis_dashboard');

  const [favorites, setFavorites] = useLocalStorageJson(
    'portfolio_analysis_favorites',
    {},
  );

  const transformedData = useMemo(() => {
    const seenMatrikels = new Set();
    const deduped = (data.portfolio_analysis_list as any[]).filter((item) => {
      if (!item.matrikel_key) return true;
      if (seenMatrikels.has(item.matrikel_key)) return false;
      seenMatrikels.add(item.matrikel_key);
      return true;
    });
    return {
      ...data,
      portfolio_analysis_list: (deduped as any[]).map((item, index) => ({
        ...item,
        id: `matrikel_${item.matrikel_key ?? index}`,
        average_weekday_night: item.average_weekday_night ?? null,
        inactive_active_ratio: item.inactive_active_ratio ?? null,
        favorites: Boolean(favorites[item.matrikel_key]),
        meter_in_sync: item.meter_in_sync ?? true,
      })),
    };
  }, [data, favorites]);

  // Build flat rows with paths for tree data: matrikel parents + meter children (all energy types)
  const treeRows = useMemo(() => {
    const rows: any[] = (transformedData.portfolio_analysis_list as any[]).map(
      (parentRow) => ({
        ...parentRow,
        path: [parentRow.matrikel_key ?? parentRow.id],
      }),
    );

    if (!matrikelList) return rows;

    (transformedData.portfolio_analysis_list as any[]).forEach((parentRow) => {
      const matrikelItem = matrikelList.find(
        (m) => m.metadata.MatrikelKey === parentRow.matrikel_key,
      );
      if (!matrikelItem) return;

      ['power', 'water', 'heat'].forEach((energyType) => {
        const meters: any[] = matrikelItem[energyType]?.meter_list ?? [];
        meters.forEach((entry, idx) => {
          const meterId =
            entry.metadata.UnitId ||
            entry.metadata.MeteringPointId ||
            entry.metadata.ReMoniUnitId ||
            `idx_${idx}`;
          rows.push({
            ...entry.analysis,
            ...entry.metadata,
            favorites: parentRow.favorites,
            id: `meter_${energyType}_${meterId}`,
            path: [
              parentRow.matrikel_key ?? parentRow.id,
              `${energyType}_${meterId}`,
            ],
            matrikel_key: parentRow.matrikel_key,
            main_address: parentRow.main_address,
            display_name:
              entry.metadata.DisplayName ||
              entry.metadata.Name ||
              entry.analysis?.display_name,
            type: energyType,
            [`${energyType}_total_consumption_year`]:
              entry.analysis?.total_consumption_year,
            average_weekday_night:
              entry.analysis?.average_weekday_night ?? null,
            inactive_active_ratio:
              entry.analysis?.inactive_active_ratio ?? null,
          });
        });
      });
    });

    return rows;
  }, [transformedData.portfolio_analysis_list, matrikelList]);

  const getTreeDataPath = useCallback((row: any) => row.path as string[], []);

  const preset = useMemo(
    () => ({
      order: [
        'favorites',
        'type',
        'linked_energy_groups',
        'power_consumptionPrYPrM2',
        'heat_consumptionPrYPrM2',
        'water_consumptionPrYPrM2',
        'AverageBuildingYear',
        'SumBuildingArea',
        'ExtractedBuildingUse',
        'ExtractedHeatInstallation',
        'EnergyLabelClassification',
        'warnings',
      ],
      sortingModel: [
        {
          field: GRID_TREE_DATA_GROUPING_FIELD,
          sort: 'asc' as GridSortDirection,
        },
      ],
      filterModel: {
        items: [],
      },
      columnGroupingModel: [
        {
          groupId: 'Benchmarking',
          headerName: t('columnGroups.benchmarkingData.header'),
          description: t('columnGroups.benchmarkingData.description'),
          children: [
            { field: 'power_consumptionPrYPrM2' },
            { field: 'heat_consumptionPrYPrM2' },
            { field: 'water_consumptionPrYPrM2' },
          ],
        },
        {
          groupId: 'Ejendomsdata',
          headerName: t('columnGroups.propertyData.header'),
          description: t('columnGroups.propertyData.description'),
          children: [
            { field: 'AverageBuildingYear' },
            { field: 'SumBuildingArea' },
          ],
        },
        {
          groupId: 'Kategorisering',
          headerName: t('columnGroups.categorisation.header'),
          description: t('columnGroups.categorisation.description'),
          children: [
            { field: 'ExtractedBuildingUse' },
            { field: 'ExtractedHeatInstallation' },
            { field: 'EnergyLabelClassification' },
          ],
        },
      ],
    }),
    [t],
  );

  const apiRef = useGridApiRef();

  const totalColumns = useDashboardColumns(transformedData, preset);

  const [isFavoritesFiltered, setIsFavoritesFiltered] = useState(false);
  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    preset.filterModel,
  );

  // ✅ Derive final filter model by merging user filters with favorites filter
  const computedFilterModel = useMemo(() => {
    let items = [...filterModel.items];
    // Remove any existing favorites filter to avoid duplicates
    items = items.filter((item) => item.field !== 'favorites');

    if (isFavoritesFiltered) {
      items.push({
        id: 'favorites-filter',
        field: 'favorites',
        operator: 'is',
        value: true,
      });
    }
    return { ...filterModel, items };
  }, [filterModel, isFavoritesFiltered]);

  const [datagridState, setDatagridState] = useState({});

  function generateVisibilityModel(
    order: string[],
    cols: GridColDef[],
  ): Record<string, boolean> {
    const columnVisibilityModel: Record<string, boolean> = {};
    cols.forEach((column) => {
      if (column.field != '__row_group_by_columns_group__')
        columnVisibilityModel[column.field] = false;
    });

    order.forEach((column) => {
      if (Object.prototype.hasOwnProperty.call(columnVisibilityModel, column)) {
        columnVisibilityModel[column] = true;
      }
    });
    return columnVisibilityModel;
  }

  const initialState = {
    columns: {
      columnVisibilityModel: {
        ...generateVisibilityModel(preset.order, totalColumns as GridColDef[]),
      },
    },
    pinnedColumns: {
      left: [
        'favorites',
        'type',
        'linked_energy_groups',
        GRID_TREE_DATA_GROUPING_FIELD,
      ],
    },
    sorting: {
      sortModel: [...preset.sortingModel],
    },
    filter: {
      filterModel: {
        items: [...preset.filterModel.items],
        quickFilterExcludeHiddenColumns: false,
      },
    },
    density: 'compact' as const,
  };

  const handleCellClick = (params: GridCellParams) => {
    if (params.field === 'favorites') {
      if (params.rowNode.depth === 0) {
        setFavorites((prev: Record<string, unknown>) => ({
          ...prev,
          [params.row.matrikel_key]: !prev[params.row.matrikel_key],
        }));
      }
      return;
    }
    const isMeterChild = params.rowNode.depth > 0;
    setMeterType(isMeterChild ? 'meter' : 'matrikel');
    setSelectedMeter(params.row);
  };

  const handleFavoritesFilterClick = (bool: boolean) => {
    setIsFavoritesFiltered(bool);
  };

  const [isCollapsed, setIsCollapsed] = useState(true);

  const handleExpandCollapse = useCallback(() => {
    treeRows.forEach((row) => {
      if (row.path.length === 1) {
        apiRef.current?.setRowChildrenExpansion(row.id, isCollapsed);
      }
    });
    setIsCollapsed((prev) => !prev);
  }, [isCollapsed, treeRows, apiRef]);

  return (
    <>
      <DataGridPremium
        showToolbar
        localeText={localeText}
        slots={{
          toolbar: PortfolioAnalysisCombinedToolbar as any,
          columnsManagement: CustomColumnsPanel as any,
          noRowsOverlay: () => (
            <CustomNoRowsOverlay rows={t('noRows')} isLoading={isLoading} />
          ),
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            handleFavoritesFilterClick: handleFavoritesFilterClick,
            isFavoritesFiltered: isFavoritesFiltered,
            setIsFavoritesFiltered: setIsFavoritesFiltered,
            handleExpandCollapse: handleExpandCollapse,
            isCollapsed: isCollapsed,
          } as any,
          columnsManagement: {
            columnGroupingModel: preset.columnGroupingModel,
          } as any,
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'skeleton',
          },
        }}
        loading={isLoading}
        treeData
        getTreeDataPath={getTreeDataPath}
        groupingColDef={{
          headerName: t('columns.main_address.label'),
          width: 350,
          valueGetter: (value, row) => row.display_name || row.address || value,
          sortable: true,
        }}
        rows={treeRows}
        columns={totalColumns}
        showCellVerticalBorder={false}
        showColumnVerticalBorder={true}
        columnGroupingModel={preset.columnGroupingModel}
        apiRef={apiRef}
        filterModel={computedFilterModel}
        onFilterModelChange={(newModel) => {
          const filteredItems = newModel.items.filter(
            (item) => item.field !== 'favorites',
          );
          setFilterModel({ ...newModel, items: filteredItems });
        }}
        initialState={initialState}
        onCellClick={handleCellClick}
        sortingOrder={['desc', 'asc', null]}
        getRowClassName={(params) =>
          params.row.type_of_mp === 'PV' ? 'solar-row' : ''
        }
        onColumnVisibilityModelChange={(newModel) => {
          setDatagridState({
            ...datagridState,
            columnVisibilityModel: newModel,
          });
        }}
        sx={{
          '& .cell-left-border': {
            borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'break-spaces',
            lineHeight: 1.5,
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-columnHeaderTitleContainerContent': {
            height: '100%',
          },
          '&.MuiDataGrid-root .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer':
            {
              pl: 1,
            },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />
    </>
  );
};

export default PortfolioAnalysisBuildingsDatagrid;
